/** **Refresh from Google…** (plan §3, D5, §9.6): a proposal, never a write. Opening it asks
 *  the server for the activity's Google place (`POST …/google_place/lookup/` — one billed
 *  Places call, at most 10 a minute per operator) and shows what Google would change: each
 *  field with the value the draft shows now → Google's (in the form the save stores), then
 *  what Google agrees with, what a field refuses (the server's reason) and what Google has
 *  nothing for. **Put N changes in the draft** sets those values as ordinary draft changes —
 *  one undoable step, "From Google" — and opens the editor when it is closed, so the operator
 *  previews, edits and saves them through the activity changeset like any other change.
 *  Nothing is written until that Save.
 *
 *  A value the draft already holds is not proposed again; one the operator changed differently
 *  says Google's replaces it. Each proposed field carries its feed verdict (D4, edit_context
 *  `feed.verdicts`, through `config.verdictOf`) before it goes in: the sync icon and "<Source>'s
 *  next sync will undo this" (amber) or "… may undo this" (muted). A row whose value is an id
 *  (the country) is named by the server — its `from_display` / `to_display` — and the editor
 *  remembers the proposed name, so the field and the ledger say it too. Refusals in the
 *  operator's terms, with Retry where another try may answer (Google down, the per-minute
 *  limit with its seconds). The page offers it to
 *  `console_activities_manage`, disabled with the reason when the activity has no Google place,
 *  no operator, editing is switched off or the editor is not loaded. */
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SyncIcon from '@mui/icons-material/Sync';
import { Alert, Box, Button, LinearProgress, Link, Popover, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useId, useMemo } from 'react';

import type { ActivityDetail } from '@/api/activities';
import { actionError, lookupGooglePlace, type ActivityGooglePlaceLookup } from '@/api/activityActions';
import { CATALOG_EDITING_OFF, CATALOG_NO_OPERATOR } from '@/features/catalog/editGate';
import { draftValuesEqual, type DraftChange, type DraftState, type RecordKey } from '@/features/records/draft/changeSet';
import { useDraft } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import type { RecordEditorConfig, RecordVerdict } from '@/features/records/editor/types';
import { formatZoned } from '@/utils/zonedTime';

import { ACTIVITY_TIMEZONE, activityRec } from '../editorConfig';

const PAPER_SX = { p: 1.5, mt: 0.5, width: 420, maxWidth: 'calc(100vw - 32px)', display: 'grid', gap: 1 } as const;
const LIST_SX = { listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.5 } as const;

/** The draft's undo label for the step. */
export const FROM_GOOGLE_LABEL = 'From Google';

/** Why the item is disabled — no place to ask Google about. */
export const GOOGLE_NO_PLACE = 'No Google place linked';

/** Why the editor is not here yet to take the proposal. */
export const EDITOR_LOADING = 'The editor is still loading';

/** Why Refresh from Google… is offered disabled, or null: the switch's words first (the server
 *  refuses the lookup while it is off), then no operator, no place, an editor that failed to
 *  load (`editBlockedReason`) or is not here yet — the proposal goes into its draft. */
export function googleRefreshBlockedReason({
  editingDisabled,
  detail,
  editBlockedReason,
  ready,
}: {
  editingDisabled: boolean;
  detail: Pick<ActivityDetail, 'tenant' | 'place' | 'place_id'>;
  editBlockedReason: string | null;
  ready: boolean;
}): string | null {
  if (editingDisabled) return CATALOG_EDITING_OFF;
  if (!detail.tenant) return CATALOG_NO_OPERATOR;
  if (!(detail.place?.id || detail.place_id)) return GOOGLE_NO_PLACE;
  if (editBlockedReason) return editBlockedReason;
  return ready ? null : EDITOR_LOADING;
}

/** One proposed change as the popover lists it. */
export interface GoogleProposal {
  field: string;
  /** What the draft shows now: its change, else the stored value. */
  now: unknown;
  to: unknown;
  /** The operator changed it in the draft: Google's value replaces that change. */
  replacesDraft: boolean;
  /** A row whose value is an id (the country): the server's names of the stored value and
   *  Google's (`from_display` / `to_display`). */
  fromDisplay?: string | null;
  toDisplay?: string | null;
}

/** Google's changes against the draft: those it would change, each with the value shown now. */
export function googleProposals(
  lookup: Pick<ActivityGooglePlaceLookup, 'changes'>,
  rec: RecordKey,
  state: DraftState,
  baselineOf: (rec: RecordKey, path: string) => unknown,
): GoogleProposal[] {
  return lookup.changes.flatMap((c) => {
    const entry = state.entries[rec]?.[c.field];
    const now = entry ? entry.to : baselineOf(rec, c.field);
    if (draftValuesEqual(now, c.to)) return [];
    return [
      {
        field: c.field,
        now,
        to: c.to,
        replacesDraft: entry !== undefined,
        ...(c.from_display !== undefined ? { fromDisplay: c.from_display } : {}),
        ...(c.to_display !== undefined ? { toDisplay: c.to_display } : {}),
      },
    ];
  });
}

/** What the feed's next run does to a proposed value, in words — none when it keeps it. */
export function proposalVerdictText(
  verdict: RecordVerdict | null,
  config: Pick<RecordEditorConfig, 'managedBy' | 'feedEvent'>,
): { text: string; undone: boolean } | null {
  if (!verdict || (verdict.verdict !== 'overwrites' && verdict.verdict !== 'may_overwrite')) return null;
  const run = `${config.managedBy ?? 'The source'}'s next ${config.feedEvent ?? 'update'}`;
  return verdict.verdict === 'overwrites'
    ? { text: `${run} will undo this`, undone: true }
    : { text: `${run} may undo this`, undone: false };
}

/** The lookup's refusal, and whether asking again may answer. */
export function googleRefusal(e: unknown): { text: string; retry: boolean } {
  const a = actionError(e);
  switch (a?.code) {
    case 'throttled':
      return {
        text: `Google was asked too often for this operator — try again ${a.retry_after_s ? `in ${a.retry_after_s} s` : 'in a minute'}.`,
        retry: true,
      };
    case 'no_place':
      return { text: 'This activity has no Google place to ask about.', retry: false };
    case 'disabled':
      return { text: 'Editing is switched off on this environment.', retry: false };
    case 'google_error':
      return { text: `Google could not answer: ${a.detail}`, retry: true };
    case 'google_not_configured':
      return { text: 'No Google Places key is configured on the server.', retry: false };
    default:
      if (a?.detail) return { text: a.detail, retry: false };
      return { text: e instanceof Error && e.message ? e.message : 'Google could not be asked.', retry: true };
  }
}

export function GoogleRefreshPopover({
  detail,
  anchorEl,
  onClose,
  onApplied,
}: {
  detail: ActivityDetail;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** The proposal is in the draft (`count` changes): the page opens the editor. */
  onApplied: (count: number) => void;
}) {
  const titleId = useId();
  const open = Boolean(anchorEl);
  const lookup = useMutation({ mutationFn: () => lookupGooglePlace(detail.id) });
  const { state, dispatch, baselineOf } = useDraft();
  const { config, fieldOf, formatValue, rememberOptions } = useRecordEditor();
  const rec = activityRec(detail.id);

  // One lookup per opening (each is a billed call); a closed popover forgets it.
  useEffect(() => {
    if (open) lookup.mutate();
    else lookup.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const data = lookup.data ?? null;
  const proposals = useMemo(
    () => (data ? googleProposals(data, rec, state, baselineOf) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, rec, state.version, baselineOf],
  );
  const label = (field: string) => fieldOf('activity', field)?.label ?? field;
  const show = (field: string, value: unknown) => {
    const f = fieldOf('activity', field);
    return f ? formatValue(f, rec, value) : String(value ?? '—');
  };
  // An id row by the server's names: the stored value's only while the draft has not changed
  // it (a draft value is named by the editor, which picked it).
  const showNow = (p: GoogleProposal) => (!p.replacesDraft && p.fromDisplay ? p.fromDisplay : show(p.field, p.now));
  const showTo = (p: GoogleProposal) => p.toDisplay || show(p.field, p.to);
  const verdictLine = (field: string) => {
    const f = fieldOf('activity', field);
    return f ? proposalVerdictText(config.verdictOf(rec, f, state), config) : null;
  };
  const refused = lookup.isError ? googleRefusal(lookup.error) : null;

  const apply = () => {
    // The field and the ledger name an id as the popover did.
    for (const p of proposals) {
      if (typeof p.to === 'number' && p.toDisplay) rememberOptions(p.field, [{ id: p.to, label: p.toDisplay }]);
    }
    const changes: DraftChange[] = proposals.map((p) => ({ rec, path: p.field, from: baselineOf(rec, p.field), to: p.to }));
    dispatch({ type: 'SET', changes, label: FROM_GOOGLE_LABEL });
    onApplied(changes.length);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-labelledby': titleId,
          'aria-busy': lookup.isPending || undefined,
          'data-google-refresh': data ? 'proposal' : refused ? 'refused' : 'asking',
          sx: PAPER_SX,
        } as object,
      }}
    >
      <Typography id={titleId} variant="body2" sx={{ fontWeight: 600 }}>
        Refresh from Google
      </Typography>

      {lookup.isPending && (
        <>
          <Typography variant="body2" component="p" sx={{ m: 0 }} role="status">
            Asking Google for this activity’s place…
          </Typography>
          <LinearProgress aria-label="Asking Google" sx={{ height: 2 }} />
        </>
      )}

      {refused && (
        <>
          <Alert severity="warning" sx={{ py: 0, '& .MuiAlert-message': { typography: 'body2', py: 0.75 } }}>
            Nothing was proposed: {refused.text}
          </Alert>
          <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onClose} autoFocus={!refused.retry}>
              Close
            </Button>
            {refused.retry && (
              <Button size="small" variant="outlined" onClick={() => lookup.mutate()} autoFocus>
                Retry
              </Button>
            )}
          </Box>
        </>
      )}

      {data && (
        <>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
            {data.place.link ? (
              <Link href={data.place.link} target="_blank" rel="noopener noreferrer" underline="hover">
                {data.place.name || 'Google’s place'}
                <OpenInNewIcon aria-hidden sx={{ fontSize: 12, ml: 0.25, verticalAlign: '-1px' }} />
              </Link>
            ) : (
              data.place.name || 'Google’s place'
            )}
            {` · asked ${formatZoned(data.fetched_ts, ACTIVITY_TIMEZONE, { weekday: false })}`}
          </Typography>

          {proposals.length > 0 ? (
            <Box component="ul" sx={LIST_SX} aria-label="Google’s changes" data-google-proposals>
              {proposals.map((p) => (
                <Box component="li" key={p.field} data-google-change={p.field} sx={{ typography: 'body2' }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {label(p.field)}
                  </Box>
                  {': '}
                  <Box component="s" sx={{ color: 'text.secondary', overflowWrap: 'anywhere' }}>
                    {showNow(p)}
                  </Box>
                  {' → '}
                  <Box component="span" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {showTo(p)}
                  </Box>
                  {p.replacesDraft && (
                    <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                      Replaces your change in the draft.
                    </Typography>
                  )}
                  <ProposalVerdict verdict={verdictLine(p.field)} />
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" component="p" sx={{ m: 0 }} data-google-nothing>
              Google agrees with every value it has — nothing to change.
            </Typography>
          )}

          {data.unchanged.length > 0 && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
              Google agrees: {data.unchanged.map(label).join(', ')}.
            </Typography>
          )}
          {data.skipped.length > 0 && (
            <Box component="ul" sx={LIST_SX} aria-label="Not taken" data-google-skipped>
              {data.skipped.map((s) => (
                <Typography
                  component="li"
                  key={s.field}
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}
                >
                  <InfoOutlinedIcon aria-hidden sx={{ fontSize: 14, mt: '1px', flex: 'none' }} />
                  <span>
                    Not taken — {label(s.field)}: {s.reason}
                  </span>
                </Typography>
              ))}
            </Box>
          )}
          {data.missing.length > 0 && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
              Google has nothing for {data.missing.map(label).join(', ')} — left as they are.
            </Typography>
          )}

          {proposals.length > 0 && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
              They go into the draft: nothing is saved until you save it.
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onClose} autoFocus={proposals.length === 0}>
              {proposals.length > 0 ? 'Cancel' : 'Close'}
            </Button>
            {proposals.length > 0 && (
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={apply} autoFocus data-google-apply>
                Put {proposals.length === 1 ? '1 change' : `${proposals.length} changes`} in the draft
              </Button>
            )}
          </Box>
        </>
      )}
    </Popover>
  );
}

/** A proposed field's feed verdict: the sync icon and the words — amber when the next run
 *  undoes the value, muted when it may. */
function ProposalVerdict({ verdict }: { verdict: { text: string; undone: boolean } | null }) {
  if (!verdict) return null;
  return (
    <Typography
      variant="caption"
      component="span"
      color={verdict.undone ? 'text.primary' : 'text.secondary'}
      sx={{ display: 'flex', gap: 0.25, alignItems: 'flex-start' }}
      data-google-verdict={verdict.undone ? 'overwrites' : 'may_overwrite'}
    >
      <SyncIcon
        aria-hidden
        sx={{ fontSize: 14, mt: '2px', flex: 'none', color: verdict.undone ? 'warning.main' : 'text.secondary' }}
      />
      <span>{verdict.text}</span>
    </Typography>
  );
}
